import { createContext, useContext, useState, useMemo, useCallback } from "react";

// NOTE: Any lint rule flagging "password" / "newPassword" / "resetToken" below
// as hardcoded secrets is a FALSE POSITIVE — these are localized UI labels for
// the respective form fields, not credentials.
const STRINGS = {
  es: {
    appName: "ChefSync P&L",
    tagline: "Control financiero, sin esfuerzo.",
    login: "Iniciar sesión",
    register: "Registrarse",
    logout: "Cerrar sesión",
    email: "Correo electrónico",
    password: "Contraseña",
    name: "Nombre",
    newPassword: "Nueva contraseña",
    forgotPassword: "¿No recuerdas tu contraseña?",
    sendReset: "Enviar enlace de recuperación",
    resetTitle: "Restablecer contraseña",
    resetToken: "Token de recuperación",
    resetSubmit: "Restablecer",
    dontHaveAccount: "¿No tienes cuenta?",
    alreadyHaveAccount: "¿Ya tienes cuenta?",
    createAccount: "Crear cuenta",
    dashboard: "Panel",
    invoices: "Gastos",
    categories: "Categorías",
    settings: "Ajustes",
    netBalance: "Balance neto",
    totalIncome: "Ingresos totales",
    totalExpense: "Gastos totales",
    incomeVsExpense: "Ingresos vs Gastos",
    profit: "Beneficio",
    byCategory: "Por categoría",
    recentInvoices: "Facturas recientes",
    addInvoice: "Añadir gasto",
    uploadInvoice: "Subir gasto",
    expensesPageTitle: "Tus gastos",
    bulkUpload: "Subir varias facturas",
    bulkUploadHint: "Sube varias facturas a la vez. La IA extraerá los datos automáticamente.",
    subtotal: "Base imponible",
    baseImponible: "Base imponible",
    baseImponibleShort: "Base imp.",
    incomePage: "Ingresos",
    incomePageTitle: "Tus ingresos",
    salariesPage: "Salarios",
    salariesPageTitle: "Tus salarios",
    addIncome: "Añadir ingreso",
    addSalary: "Añadir salario",
    uploadIncome: "Subir ingreso",
    uploadSalary: "Subir salario",
    bulkUploadIncome: "Subir varios ingresos",
    bulkUploadSalaries: "Subir varios salarios",
    plan: "Plan",
    planFree: "Free",
    planBasic: "Basic",
    planPro: "Pro",
    planBusiness: "Business",
    currentPlan: "Tu plan",
    upgrade: "Mejorar plan",
    upgradePro: "Mejorar plan",
    downgradeFree: "Cambiar a Free",
    monthly: "Mensual",
    yearly: "Anual",
    yearlyDiscount: "Ahorra 2 meses",
    perMonth: "/mes",
    perYear: "/año",
    invoicesPerMonth: "facturas / mes",
    invoicesUnlimited: "Facturas ilimitadas",
    selectThisPlan: "Elegir este plan",
    yourCurrentPlan: "Tu plan actual",
    paymentSoon: "Próximamente — los pagos automáticos llegarán pronto. Mientras tanto, contacta para activar el plan.",
    proRequired: "Función no disponible",
    proRequiredHelp: "Esta función no está activa en tu plan.",
    monthlyUsage: "Uso este mes",
    invoicesUsed: "Facturas creadas",
    invoicesRemaining: "Restantes",
    limitReached: "Has alcanzado el límite mensual",
    billing: "Facturación",
    planUnlimited: "Sin límites",
    allFeaturesIncluded: "Todas las funciones incluidas",
    chooseYourPlan: "Elige tu plan",
    pricingSubtitle: "Cambia o cancela cuando quieras. Pagos seguros vía Stripe (próximamente).",
    pickSalaryCategory: "Selecciona una categoría de salarios",
    creditNote: "Abono / Factura rectificativa",
    creditNoteShort: "Abono",
    creditNoteHelp: "Marca esta opción si la factura es un abono o factura rectificativa: el importe se sumará en positivo y se restará de los gastos.",
    total: "Total",
    compareWith: "Comparar con",
    noComparison: "Sin comparar",
    vsYear: "vs",
    products: "Productos",
    budget: "Presupuesto",
    productsTitle: "Productos y precios",
    priceHistory: "Historial de precios",
    occurrences: "Veces vista",
    firstPrice: "Primer precio",
    lastPrice: "Último precio",
    minPrice: "Mínimo",
    maxPrice: "Máximo",
    change: "Cambio",
    noProducts: "Aún no se han detectado productos. Sube facturas con líneas de detalle para verlas aquí.",
    sortByChange: "Por mayor incremento",
    sortByName: "Por nombre",
    sortByOccurrences: "Por nº de apariciones",
    sortByLastPrice: "Por último precio",
    minAmount: "Importe mínimo",
    maxAmount: "Importe máximo",
    selected: "seleccionadas",
    bulkChangeCategory: "Cambiar categoría",
    bulkDelete: "Eliminar",
    deselect: "Deseleccionar",
    confirmBulkDelete: "¿Eliminar las facturas seleccionadas?",
    bulkUpdated: "Actualizadas",
    bulkDeleted: "Eliminadas",
    lineItems: "Líneas de detalle",
    addLineItem: "Añadir línea",
    productName: "Producto",
    qty: "Cant.",
    unitPrice: "Precio unitario",
    lineTotal: "Total",
    dragDrop: "Arrastra y suelta un PDF o imagen aquí, o haz clic para seleccionar",
    extractWithAI: "Extraer con IA",
    extracting: "Extrayendo datos...",
    vendor: "Proveedor",
    vendors: "Proveedores",
    date: "Fecha",
    invoiceDate: "Fecha factura",
    salaryDate: "Fecha salario",
    uploadDate: "Fecha subida",
    amount: "Importe",
    currency: "Moneda",
    category: "Categoría",
    type: "Tipo",
    expense: "Gasto",
    income: "Ingreso",
    notes: "Notas",
    invoiceNumber: "Número de factura",
    tax: "Impuesto",
    vendorTaxId: "CIF / NIF Fiscal",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    export: "Exportar CSV",
    filter: "Filtrar",
    all: "Todas",
    search: "Buscar...",
    searchPlaceholder: "Buscar por proveedor, CIF o nº factura...",
    searchPlaceholderConcept: "Buscar por concepto...",
    concept: "Concepto",
    actions: "Acciones",
    noInvoices: "Aún no hay facturas. ¡Sube tu primera factura!",
    addCategory: "Añadir categoría",
    color: "Color",
    labelEs: "Etiqueta (ES)",
    labelEn: "Etiqueta (EN)",
    saved: "Guardado",
    deleted: "Eliminado",
    needsReview: "Revisar",
    onlyReview: "Solo a revisar",
    uploadFirst: "Sube primero un archivo o introduce los datos manualmente.",
    manualEntry: "Entrada manual",
    aiEntry: "Extracción con IA",
    file: "Archivo",
    welcome: "Bienvenido",
    confirmDelete: "¿Eliminar este registro?",
    baseCurrency: "Moneda base",
    language: "Idioma",
    profile: "Perfil",
    accountSaved: "Cuenta guardada",
    invoicePreview: "Vista previa",
    period: "Periodo",
    priceAlert: "Alerta de precio",
    priceAlertThreshold: "Umbral de alerta (%)",
    priceAlertHelp: "Avísame cuando un precio suba o baje más de este % entre facturas.",
    aboveThreshold: "Supera el umbral",
    exportAeat: "Exportar AEAT/SII",
    sortAsc: "Ascendente",
    sortDesc: "Descendente",
    sortBy: "Ordenar por",
    taxRate: "Tipo IVA",
    taxRateHelp: "España: 0% (exento), 4%, 10%, 21%.",
    taxExempt: "Sin IVA (exento)",
    inputMode: "Modo de entrada",
    inputModeBase: "Introduzco base",
    inputModeTotal: "Introduzco total",
    incomeBase: "Ingresos (base)",
    incomeIva: "IVA repercutido",
    incomeTotal: "Total Facturado",
    expenseBase: "Gastos (base)",
    expenseIva: "IVA soportado",
    expenseTotal: "Total pagado",
    ivaBalance: "Balance IVA",
    ivaBalanceHelp: "IVA repercutido − IVA soportado. Positivo: a ingresar a Hacienda. Negativo: a compensar.",
    quarterlySummary: "Resumen trimestral",
    quarter: "Trimestre",
    foodCostPct: "% Food Cost",
    salaryPct: "% Coste Personal",
    primeCostPct: "% Prime Cost",
    foodCostHelp: "Food Cost = Coste de alimentos (B.I.) / Facturación (B.I.). Todo sin IVA. Objetivo restaurante: 28-35%.",
    salaryHelp: "Coste de Personal = Salarios (B.I.) / Facturación (B.I.). Todo sin IVA. Objetivo restaurante: 25-35%.",
    primeCostHelp: "Prime Cost = Food Cost + Coste Personal. Objetivo: ≤ 60%.",
    monthlyRatios: "Ratios mensuales (sobre Base Imponible)",
    ratiosOverBase: "Calculado sobre base imponible (sin IVA)",
    revenueBase: "Facturación (B.I.)",
    kind: "Tipo de coste",
    kindFoodCost: "Food cost (sin IVA)",
    kindSalary: "Salarios (sin IVA)",
    kindOther: "Otros",
  },
  en: {
    appName: "ChefSync P&L",
    tagline: "Effortless financial control.",
    login: "Sign in",
    register: "Sign up",
    logout: "Sign out",
    email: "Email",
    password: "Password",
    name: "Name",
    newPassword: "New password",
    forgotPassword: "Forgot your password?",
    sendReset: "Send reset link",
    resetTitle: "Reset password",
    resetToken: "Reset token",
    resetSubmit: "Reset",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    createAccount: "Create account",
    dashboard: "Dashboard",
    invoices: "Expenses",
    categories: "Categories",
    settings: "Settings",
    netBalance: "Net balance",
    totalIncome: "Total income",
    totalExpense: "Total expense",
    incomeVsExpense: "Income vs Expense",
    profit: "Profit",
    byCategory: "By category",
    recentInvoices: "Recent invoices",
    addInvoice: "Add expense",
    uploadInvoice: "Upload expense",
    expensesPageTitle: "Your expenses",
    bulkUpload: "Upload multiple invoices",
    bulkUploadHint: "Upload many invoices at once. AI will extract the data automatically.",
    subtotal: "Subtotal",
    baseImponible: "Net amount",
    baseImponibleShort: "Net",
    incomePage: "Income",
    incomePageTitle: "Your income",
    salariesPage: "Salaries",
    salariesPageTitle: "Your salaries",
    addIncome: "Add income",
    addSalary: "Add salary",
    uploadIncome: "Upload income",
    uploadSalary: "Upload salary",
    bulkUploadIncome: "Upload multiple incomes",
    bulkUploadSalaries: "Upload multiple salaries",
    plan: "Plan",
    planFree: "Free",
    planBasic: "Basic",
    planPro: "Pro",
    planBusiness: "Business",
    currentPlan: "Your plan",
    upgrade: "Change plan",
    upgradePro: "Change plan",
    downgradeFree: "Switch to Free",
    monthly: "Monthly",
    yearly: "Yearly",
    yearlyDiscount: "Save 2 months",
    perMonth: "/mo",
    perYear: "/yr",
    invoicesPerMonth: "invoices / month",
    invoicesUnlimited: "Unlimited invoices",
    selectThisPlan: "Select this plan",
    yourCurrentPlan: "Your current plan",
    paymentSoon: "Coming soon — automated payments are on the way. Meanwhile contact us to activate the plan.",
    proRequired: "Feature unavailable",
    proRequiredHelp: "This feature is not enabled on your plan.",
    monthlyUsage: "This month",
    invoicesUsed: "Invoices created",
    invoicesRemaining: "Remaining",
    limitReached: "Monthly limit reached",
    billing: "Billing",
    planUnlimited: "Unlimited",
    allFeaturesIncluded: "All features included",
    chooseYourPlan: "Choose your plan",
    pricingSubtitle: "Change or cancel any time. Secure payments via Stripe (coming soon).",
    pickSalaryCategory: "Pick a salaries category",
    creditNote: "Credit note / refund",
    creditNoteShort: "Refund",
    creditNoteHelp: "Tick this if the invoice is a credit note: the amount will count as positive and will reduce expenses.",
    total: "Total",
    compareWith: "Compare with",
    noComparison: "No comparison",
    vsYear: "vs",
    products: "Products",
    budget: "Budget",
    productsTitle: "Products & prices",
    priceHistory: "Price history",
    occurrences: "Seen",
    firstPrice: "First price",
    lastPrice: "Last price",
    minPrice: "Min",
    maxPrice: "Max",
    change: "Change",
    noProducts: "No products detected yet. Upload invoices with line items to see them here.",
    sortByChange: "Biggest increase",
    sortByName: "By name",
    sortByOccurrences: "By occurrences",
    sortByLastPrice: "By last price",
    minAmount: "Min amount",
    maxAmount: "Max amount",
    selected: "selected",
    bulkChangeCategory: "Change category",
    bulkDelete: "Delete",
    deselect: "Deselect",
    confirmBulkDelete: "Delete selected invoices?",
    bulkUpdated: "Updated",
    bulkDeleted: "Deleted",
    lineItems: "Line items",
    addLineItem: "Add line",
    productName: "Product",
    qty: "Qty",
    unitPrice: "Unit price",
    lineTotal: "Total",
    dragDrop: "Drag & drop a PDF or image here, or click to select",
    extractWithAI: "Extract with AI",
    extracting: "Extracting data...",
    vendor: "Vendor",
    vendors: "Vendors",
    date: "Date",
    invoiceDate: "Invoice date",
    salaryDate: "Salary date",
    uploadDate: "Upload date",
    amount: "Amount",
    currency: "Currency",
    category: "Category",
    type: "Type",
    expense: "Expense",
    income: "Income",
    notes: "Notes",
    invoiceNumber: "Invoice number",
    tax: "Tax",
    vendorTaxId: "Vendor Tax ID",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    export: "Export CSV",
    filter: "Filter",
    all: "All",
    search: "Search...",
    searchPlaceholder: "Search by vendor, tax ID or invoice #...",
    searchPlaceholderConcept: "Search by concept...",
    concept: "Concept",
    actions: "Actions",
    noInvoices: "No invoices yet. Upload your first invoice!",
    addCategory: "Add category",
    color: "Color",
    labelEs: "Label (ES)",
    labelEn: "Label (EN)",
    saved: "Saved",
    deleted: "Deleted",
    needsReview: "Review",
    onlyReview: "Needs review only",
    uploadFirst: "Upload a file first or enter data manually.",
    manualEntry: "Manual entry",
    aiEntry: "AI extraction",
    file: "File",
    welcome: "Welcome",
    confirmDelete: "Delete this record?",
    baseCurrency: "Base currency",
    language: "Language",
    profile: "Profile",
    accountSaved: "Account saved",
    invoicePreview: "Preview",
    period: "Period",
    priceAlert: "Price alert",
    priceAlertThreshold: "Alert threshold (%)",
    priceAlertHelp: "Notify me when a price changes by more than this % across invoices.",
    aboveThreshold: "Above threshold",
    exportAeat: "Export AEAT/SII",
    sortAsc: "Ascending",
    sortDesc: "Descending",
    sortBy: "Sort by",
    taxRate: "VAT rate",
    taxRateHelp: "Spain: 0% (exempt), 4%, 10%, 21%.",
    taxExempt: "No VAT (exempt)",
    inputMode: "Input mode",
    inputModeBase: "I enter base",
    inputModeTotal: "I enter total",
    incomeBase: "Income (base)",
    incomeIva: "VAT collected",
    incomeTotal: "Total received",
    expenseBase: "Expense (base)",
    expenseIva: "VAT paid",
    expenseTotal: "Total paid",
    ivaBalance: "VAT balance",
    ivaBalanceHelp: "VAT collected − VAT paid. Positive: owed to tax authority. Negative: to offset.",
    quarterlySummary: "Quarterly summary",
    quarter: "Quarter",
    foodCostPct: "Food Cost %",
    salaryPct: "Labor Cost %",
    primeCostPct: "Prime Cost %",
    foodCostHelp: "Food Cost = Food expense (net) / Revenue (net). All figures excl. VAT. Target restaurant: 28-35%.",
    salaryHelp: "Labor = Labor expense (net) / Revenue (net). All figures excl. VAT. Target restaurant: 25-35%.",
    primeCostHelp: "Prime Cost = Food Cost + Labor. Target: ≤ 60%.",
    monthlyRatios: "Monthly ratios (over net revenue)",
    ratiosOverBase: "Computed over net amounts (excl. VAT)",
    revenueBase: "Revenue (net)",
    kind: "Cost type",
    kindFoodCost: "Food cost (ex VAT)",
    kindSalary: "Labor (ex VAT)",
    kindOther: "Other",
  },
};

const I18nContext = createContext({ lang: "es", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "es");

  const setLangPersist = useCallback((l) => {
    setLang(l);
    localStorage.setItem("lang", l);
  }, []);

  const value = useMemo(() => {
    const dict = STRINGS[lang] || STRINGS.es;
    return {
      lang,
      setLang: setLangPersist,
      t: (key) => dict[key] || STRINGS.es[key] || key,
    };
  }, [lang, setLangPersist]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
export function categoryLabel(cat, lang) {
  if (!cat) return "";
  if (lang === "en") return cat.label_en || cat.label_es || cat.name;
  return cat.label_es || cat.label_en || cat.name;
}
